import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import {
  Home,
  Building2,
  Users,
  Settings,
  LogOut,
  Menu,
  Calendar,
  DollarSign,
  Globe,
  Bell,
  User,
  ChevronDown,
  X,
  MessageSquare,
  CreditCard,
  Download,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { ThemeToggle } from '../theme/ThemeToggle';
import LandlordSettingsModal from '../landlord/LandlordSettingsModal';
import { useToast } from '../ui/Toaster';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { usePushRegistration } from '../../hooks/usePushRegistration';
import { usePermissions } from '../../hooks/usePermissions';
import { useCloseOnRouteChange } from '../../hooks/useCloseOnRouteChange';
import { NotificationPermissionPrompt } from '../notifications/NotificationPermissionPrompt';
import { EnableNotificationsModal } from '../notifications/EnableNotificationsModal';
import { NotificationPanel } from '../notifications/NotificationPanel';
import { API_BASE_URL } from '../../config/api';
import { authTokenService } from '../../services/auth.service';
import { billingService } from '../../services/billingService';
import { displayPhoneKE, formatPhoneKEForInput, normalizePhoneKE } from '../../utils/formatters';

// Styled Components
const LayoutContainer = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.background};
  color: ${props => props.theme.foreground};
`;

const Navbar = styled.nav`
  border-bottom: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.card};
  backdrop-filter: blur(8px);
  position: sticky;
  top: 0;
  z-index: 1000;
`;

const NavbarContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 16px;
  
  @media (min-width: 640px) {
    padding: 0 24px;
  }
  
  @media (min-width: 1024px) {
    padding: 0 32px;
  }
`;

const NavbarContent = styled.div`
  display: flex;
  justify-content: space-between;
  height: 64px;
  align-items: center;
`;

const NavbarLeft = styled.div`
  display: flex;
  align-items: center;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const LogoIcon = styled(Building2)`
  height: 32px;
  width: 32px;
  color: ${props => {
    // Check if dark theme by looking at background lightness
    // Light theme background: 'hsl(0 0% 98%)' - very light (98% lightness)
    // Dark theme background: 'hsl(168 97% 10%)' - very dark (10% lightness)
    const bg = String(props.theme.background || '');
    // If background has 98% or 100% lightness, it's light theme
    // If background has 10% or lower lightness, it's dark theme
    const isLight = bg.includes('98%') || bg.includes('100%');
    // In light theme: use dark color (hsl(168 95% 12%))
    // In dark theme: use white
    return isLight ? 'hsl(168 95% 12%)' : 'white';
  }};
`;

const LogoText = styled.div`
  margin-left: 4px;
  font-size: 20px;
  font-weight: 700;
  color: ${props => props.theme.foreground};
`;

const LogoPlus = styled.span`
  color: ${props => props.theme.primary};
`;

const LogoPlusSign = styled.span`
  color: ${props => props.theme.primaryGlow};
  font-size: 18px;
  font-weight: 700;
`;

const DesktopNavigation = styled.div`
  display: none;
  margin-left: 40px;
  gap: 32px;
  
  @media (min-width: 768px) {
    display: flex;
  }
`;

const NavLink = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  border: none !important;
  background: none;
  cursor: pointer;
  color: ${props => props.$active ? props.theme.primary : props.theme.mutedForeground};
  border-bottom: 2px solid ${props => props.$active ? props.theme.primary : 'transparent'};
  
  &:hover {
    color: ${props => props.$active ? props.theme.primary : props.theme.foreground};
    border: none !important;
  }
  
  &:focus-visible {
    outline: none !important;
    border: none !important;
  }
  
  &:active {
    outline: none !important;
    border: none !important;
    border-bottom: 2px solid ${props => props.$active ? props.theme.primary : 'transparent'} !important;
  }
  
  &:focus {
    outline: none !important;
    border: none !important;
  }
  
  svg {
    height: 16px;
    width: 16px;
    margin-right: 8px;
  }
`;

const NavbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const DesktopActions = styled.div`
  display: none;
  gap: 16px;
  align-items: center;
  
  @media (min-width: 768px) {
    display: flex;
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  position: relative;
  color: ${props => props.theme.foreground};

  &:hover {
    background-color: ${props => props.theme.muted};
  }

  &:focus-visible {
    outline: none !important;
    border: none !important;
  }

  &:active {
    outline: none !important;
    border: none !important;
  }

  &:focus {
    outline: none !important;
    border: none !important;
  }

  /* Icon container for badge positioning */
  .icon-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: ${props => props.theme.destructive};
  color: white;
  font-size: 10px;
  font-weight: 600;
  border-radius: 50%;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  z-index: 10;
  pointer-events: none;
`;

const UserAvatar = styled.button`
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 12px;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: ${props => props.theme.card};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadowSoft};
  min-width: 224px;
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-8px)'};
  transition: all 0.2s ease;
`;

const DropdownHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${props => props.theme.border};
`;

const UserName = styled.div`
  font-weight: 600;
  color: ${props => props.theme.foreground};
  margin-bottom: 2px;
  font-size: 14px;
`;

const UserEmail = styled.div`
  font-size: 12px;
  color: ${props => props.theme.mutedForeground};
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${props => props.theme.foreground};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.muted};
  }

  &:focus-visible {
    outline: none !important;
    border: none !important;
  }

  &:active {
    outline: none !important;
    border: none !important;
  }

  &:focus {
    outline: none !important;
    border: none !important;
  }

  svg {
    height: 16px;
    width: 16px;
  }
`;

const MainContent = styled.main`
  padding: 0px;
  background-color: ${props => props.theme.background};
  min-height: calc(100vh - 64px);
`;

const UserMenuContainer = styled.div`
  position: relative;

  @media (max-width: 767px) {
    display: none;
  }
`;

const MobileMenuButton = styled.button`
  display: flex;
  background: none;
  border: none;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.foreground};

  &:hover {
    background-color: ${props => props.theme.muted};
  }

  &:focus-visible {
    outline: none !important;
    border: none !important;
  }

  &:active {
    outline: none !important;
    border: none !important;
  }

  &:focus {
    outline: none !important;
    border: none !important;
  }

  @media (min-width: 768px) {
    display: none;
  }
`;

const NavigationDrawer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 300px;
  height: 100vh;
  background: ${props => props.theme.card};
  border-left: 1px solid ${props => props.theme.border};
  z-index: 1001;
  transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(100%)'};
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  
  @media (min-width: 640px) {
    width: 400px;
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid ${props => props.theme.border};
`;

const DrawerLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.theme.foreground};
`;

const DrawerLogoIcon = styled(Building2)`
  height: 24px;
  width: 24px;
  color: ${props => {
    // Check if dark theme by looking at background lightness
    // Light theme background: 'hsl(0 0% 98%)' - very light (98% lightness)
    // Dark theme background: 'hsl(168 97% 10%)' - very dark (10% lightness)
    const bg = String(props.theme.background || '');
    // If background has 98% or 100% lightness, it's light theme
    // If background has 10% or lower lightness, it's dark theme
    const isLight = bg.includes('98%') || bg.includes('100%');
    // In light theme: use dark color (hsl(168 95% 12%))
    // In dark theme: use white
    return isLight ? 'hsl(168 95% 12%)' : 'white';
  }};
`;

const DrawerLogoText = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.theme.foreground};
`;

const DrawerLogoPlus = styled.span`
  color: ${props => props.theme.primary};
`;

const DrawerLogoPlusSign = styled.span`
  color: ${props => props.theme.primaryGlow};
  font-size: 16px;
  font-weight: 700;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.foreground};
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.muted};
  }

  &:focus-visible {
    outline: none !important;
    border: none !important;
  }

  &:active {
    outline: none !important;
    border: none !important;
  }

  &:focus {
    outline: none !important;
    border: none !important;
  }
`;

const DrawerContent = styled.div`
  flex: 1;
  padding: 16px 0;
`;

const DrawerNavSection = styled.div`
  margin-bottom: 24px;
`;

const DrawerNavItem = styled.button<{ $active?: boolean }>`
  width: 100%;
  background: none;
  border: none;
  padding: 12px 24px;
  color: ${props => props.$active ? props.theme.primary : props.theme.foreground};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  font-size: 16px;
  font-weight: 500;
  border-left: 3px solid ${props => props.$active ? props.theme.primary : 'transparent'};

  &:hover {
    background-color: ${props => props.theme.muted};
  }

  &:focus-visible {
    outline: none;
  }

  &:active {
    outline: none;
    border: none;
    border-left: 3px solid ${props => props.$active ? props.theme.primary : 'transparent'};
  }

  ${props => props.$active && `
    background-color: ${props.theme.primary}20;
    color: ${props.theme.primary};
  `}
  
  svg {
    height: 20px;
    width: 20px;
  }
`;

const DrawerUserSection = styled.div`
  padding: 24px;
  border-top: 1px solid ${props => props.theme.border};
`;

const DrawerUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const DrawerUserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.theme.muted};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.mutedForeground};
  font-weight: 600;
  overflow: hidden;
  flex-shrink: 0;
`;

const DrawerUserDetails = styled.div`
  flex: 1;
`;

const DrawerUserName = styled.div`
  font-weight: 600;
  color: ${props => props.theme.foreground};
  font-size: 14px;
`;

const DrawerUserEmail = styled.div`
  font-size: 12px;
  color: ${props => props.theme.mutedForeground};
`;

const DrawerOption = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  color: ${props => props.theme.foreground};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.muted};
  }
`;

const DrawerOptionText = styled.span`
  font-size: 14px;
`;

const DrawerOptionIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;

  /* Icon wrapper for badge positioning */
  .icon-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

const DrawerFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${props => props.theme.border};
`;

const DrawerFooterItem = styled.button`
  width: 100%;
  background: none;
  border: none;
  padding: 12px 0;
  color: ${props => props.theme.foreground};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  font-size: 14px;

  &:hover {
    color: ${props => props.theme.primary};
  }

  &:focus-visible {
    outline: none !important;
    border: none !important;
  }

  &:active {
    outline: none !important;
    border: none !important;
  }

  &:focus {
    outline: none !important;
    border: none !important;
  }

  svg {
    height: 16px;
    width: 16px;
  }
`;

const DrawerSectionTitle = styled.div`
  padding: 8px 24px;
  font-size: 12px;
  font-weight: 700;
  color: ${props => props.theme.mutedForeground};
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

// ── Billing Wall ────────────────────────────────────────────────────────────
// Full-screen overlay shown to landlords with an expired/suspended subscription.
// Tenants NEVER see this — they use a different layout entirely.
const BillingWallOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: ${props => props.theme.background};
  animation: bwFadeIn 0.25s ease;

  @keyframes bwFadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
`;

const BillingWallCard = styled.div`
  max-width: 480px;
  width: 100%;
  background: ${props => props.theme.card};
  border: 1px solid ${props => props.theme.border};
  border-radius: 16px;
  padding: 40px 36px;
  text-align: center;
  box-shadow: ${props => props.theme.shadowElegant};
`;

const BillingWallIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: hsl(0 84% 55% / 0.12);
  color: hsl(0 84% 55%);
  margin-bottom: 20px;
`;

const BillingWallTitle = styled.h2`
  font-size: 1.375rem;
  font-weight: 700;
  color: ${props => props.theme.foreground};
  margin-bottom: 10px;
`;

const BillingWallBody = styled.p`
  font-size: 0.9375rem;
  color: ${props => props.theme.mutedForeground};
  line-height: 1.6;
  margin-bottom: 28px;
`;

const BillingWallBtnRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BillingWallPrimaryBtn = styled.button`
  width: 100%;
  padding: 12px 20px;
  border-radius: 8px;
  border: none;
  background: ${props => props.theme.primary};
  color: ${props => props.theme.primaryForeground};
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: opacity 0.2s;

  &:hover { opacity: 0.88; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const BillingWallSecondaryBtn = styled.button`
  width: 100%;
  padding: 10px 20px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.border};
  background: transparent;
  color: ${props => props.theme.foreground};
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s;

  &:hover { background: ${props => props.theme.muted}; }
`;

const BillingPopupBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const BillingPopupCard = styled.div`
  width: 100%;
  max-width: 460px;
  background: ${props => props.theme.card};
  border: 1px solid ${props => props.theme.border};
  border-radius: 16px;
  box-shadow: ${props => props.theme.shadowElegant};
  padding: 24px;
`;

const BillingPopupHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
`;

const BillingPopupTitle = styled.h3`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: ${props => props.theme.foreground};
`;

const BillingPopupBody = styled.p`
  margin: 8px 0 0;
  font-size: 0.9375rem;
  color: ${props => props.theme.mutedForeground};
  line-height: 1.6;
`;

const BillingPopupClose = styled.button`
  border: none;
  background: transparent;
  color: ${props => props.theme.mutedForeground};
  cursor: pointer;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const BillingAmountCard = styled.div`
  border-radius: 12px;
  padding: 16px;
  background: ${props => props.theme.muted};
  margin-bottom: 16px;
`;

const BillingAmountLabel = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.mutedForeground};
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const BillingAmountValue = styled.div`
  font-size: 1.75rem;
  font-weight: 800;
  color: ${props => props.theme.foreground};
`;

const BillingField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const BillingFieldLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.theme.foreground};
`;

const BillingInput = styled.input`
  width: 100%;
  border-radius: 10px;
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.background};
  color: ${props => props.theme.foreground};
  padding: 12px 14px;
  font-size: 0.9375rem;
`;

const BillingHelperText = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: ${props => props.theme.mutedForeground};
`;

const BillingInlineMessage = styled.p<{ $error?: boolean }>`
  margin: 0 0 12px;
  font-size: 0.875rem;
  color: ${props => props.$error ? 'hsl(0 84% 60%)' : props.theme.foreground};
`;

const BillingWallBrand = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: ${props => props.theme.mutedForeground};
  margin-bottom: 28px;

  span { color: ${props => props.theme.primary}; }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;



// ── BillingWall helpers ──────────────────────────────────────────────────────
function getBillingWallMessage(reason?: string): string {
  switch (reason) {
    case 'trial_expired':
      return 'Your free trial has ended. Please activate your subscription to continue managing your properties.';
    case 'past_due':
      return 'Your account has an outstanding balance. Please update your payment method to restore full access.';
    case 'suspended':
      return 'Your account has been suspended due to non-payment. Please contact support or update your billing.';
    case 'canceled':
      return 'Your subscription has been cancelled. Please reactivate it to continue using TenantPlus.';
    case 'mandate_pending':
      return 'Please complete your billing setup (direct-debit mandate) to activate your subscription.';
    default:
      return 'Your subscription is inactive. Please update your billing details to restore access.';
  }
}

// ── BillingWall state types ─────────────────────────────────────────────────
interface BillingWallState {
  active: boolean;
  reason?: string;
  message?: string;
  redirectTo?: string;
}

const ModernLayout: React.FC = () => {
  const { user, signOut } = useAuth();
  const toast = useToast();
  const { isInstallable, isInstalled, canShowButton, handleInstall, deferredPrompt } = usePWAInstall();
  usePushRegistration(!!user);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [settingsInitialSection, setSettingsInitialSection] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { canAccess, hasPermission } = usePermissions();

  // ── Billing wall ──────────────────────────────────────────────────────────
  const [billingWall, setBillingWall] = useState<BillingWallState>({ active: false });
  const [billingChecking, setBillingChecking] = useState(false);
  const billingCheckedRef = useRef(false);
  const [billingPopupOpen, setBillingPopupOpen] = useState(false);
  const [billingPopupLoading, setBillingPopupLoading] = useState(false);
  const [billingPopupSubmitting, setBillingPopupSubmitting] = useState(false);
  const [billingPopupPolling, setBillingPopupPolling] = useState(false);
  const [billingPopupAmount, setBillingPopupAmount] = useState<number | null>(null);
  const [billingPopupPhone, setBillingPopupPhone] = useState('');
  const [billingPopupInvoiceId, setBillingPopupInvoiceId] = useState<string | null>(null);
  const [billingPopupTransactionId, setBillingPopupTransactionId] = useState<string | null>(null);
  const [billingPopupUsedMinimum, setBillingPopupUsedMinimum] = useState(false);
  const [billingPopupError, setBillingPopupError] = useState<string | null>(null);
  const [billingPopupMessage, setBillingPopupMessage] = useState<string | null>(null);

  // Proactive check on mount for LANDLORD role (fetch billing status)
  useEffect(() => {
    // Only check for landlord-side users — tenants never use this layout
    if (!user || user.role === 'TENANT' || user.role === null) return;
    if (billingCheckedRef.current) return;
    billingCheckedRef.current = true;

    (async () => {
      try {
        const token = authTokenService.getToken();
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/billing/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          // 403 BILLING_SUSPENDED is handled by the global event below;
          // other non-ok statuses (503, network) are silently ignored
          // so a transient backend error doesn't trigger the wall.
          return;
        }
        const data = await res.json();
        const gate = data?.gate;
        if (gate && gate.allowed === false) {
          setBillingWall({
            active: true,
            reason: gate.reason,
            message: getBillingWallMessage(gate.reason),
            redirectTo: gate.redirectTo,
          });
        }
      } catch {
        // Non-fatal — do not block the UI on billing check failure
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Reactive: listen for BILLING_SUSPENDED events fired by the API interceptor
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      setBillingWall({
        active: true,
        reason: detail.reason,
        message: detail.message || getBillingWallMessage(detail.reason),
        redirectTo: detail.redirectTo,
      });
    };
    window.addEventListener('billing:suspended', handler);
    return () => window.removeEventListener('billing:suspended', handler);
  }, []);

  useEffect(() => {
    if (!billingWall.active) {
      setBillingPopupOpen(false);
      setBillingPopupInvoiceId(null);
      setBillingPopupTransactionId(null);
      setBillingPopupUsedMinimum(false);
      setBillingPopupError(null);
      setBillingPopupMessage(null);
    }
  }, [billingWall.active]);

  const refreshBillingGate = async () => {
    const token = authTokenService.getToken();
    if (!token) return false;

    const res = await fetch(`${API_BASE_URL}/billing/status`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return false;
    const data = await res.json();
    if (data?.gate?.allowed === true) {
      setBillingWall({ active: false });
      return true;
    }

    if (data?.gate?.allowed === false) {
      setBillingWall({
        active: true,
        reason: data.gate.reason,
        message: getBillingWallMessage(data.gate.reason),
        redirectTo: data.gate.redirectTo,
      });
    }

    return false;
  };

  const openBillingPopup = async () => {
    setBillingPopupOpen(true);
    setBillingPopupLoading(true);
    setBillingPopupError(null);
    setBillingPopupMessage(null);
    setBillingPopupPhone((current) => current || displayPhoneKE((user as any)?.phone || ''));

    try {
      const previewRes = await billingService.getRenewalPreview();

      setBillingPopupAmount(previewRes.data.totalAmount);
      setBillingPopupInvoiceId(previewRes.data.invoiceId);
      setBillingPopupUsedMinimum(previewRes.data.usedMinimum ?? false);
      setBillingPopupTransactionId(null);

      if (previewRes.data.totalAmount <= 0) {
        setBillingPopupError('No billable amount is available for this account yet. Billing is based on your current unit count.');
      }
    } catch (error: any) {
      setBillingPopupAmount(null);
      setBillingPopupInvoiceId(null);
      setBillingPopupError(error?.response?.data?.error || error?.message || 'Could not load billing details.');
    } finally {
      setBillingPopupLoading(false);
    }
  };

  const checkBillingPaymentStatus = async (showPendingToast: boolean = false) => {
    if (!billingPopupInvoiceId) return;

    try {
      setBillingPopupPolling(true);
      setBillingPopupError(null);

      const res = await billingService.getRenewalStatus(billingPopupInvoiceId, billingPopupTransactionId || undefined);
      if (res.data.status === 'PAID') {
        setBillingPopupMessage('Payment verified successfully. Your subscription is now active.');
        setBillingPopupOpen(false);
        setBillingPopupInvoiceId(null);
        setBillingPopupTransactionId(null);
        const restored = await refreshBillingGate();
        if (restored) {
          toast.success('Subscription renewed', { description: 'Your account is now active.' });
        }
        return;
      }

      if (res.data.status === 'FAILED') {
        setBillingPopupInvoiceId(null);
        setBillingPopupTransactionId(null);
        setBillingPopupMessage(null);
        setBillingPopupError('Payment failed or was cancelled. Please initiate another STK push.');
        return;
      }

      setBillingPopupMessage('STK push sent. Complete the payment on your phone and we will verify it automatically.');
      if (showPendingToast) {
        toast.info('Payment still pending', { description: 'Complete the M-Pesa prompt on your phone, then check again.' });
      }
    } catch (error: any) {
      setBillingPopupError(error?.response?.data?.error || error?.message || 'Could not verify your billing payment yet.');
    } finally {
      setBillingPopupPolling(false);
    }
  };

  useEffect(() => {
    // Only poll after an STK push has actually been initiated (transactionId is set)
    if (!billingPopupOpen || !billingPopupInvoiceId || !billingPopupTransactionId) return;

    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      if (cancelled || !billingPopupInvoiceId) return;
      attempts += 1;
      await checkBillingPaymentStatus(false);
      if (!cancelled && attempts < 45 && billingPopupInvoiceId) {
        timer = setTimeout(poll, 4000);
      }
    };

    timer = setTimeout(poll, 4000);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [billingPopupOpen, billingPopupInvoiceId, billingPopupTransactionId]);

  const initiateBillingPayment = async () => {
    const normalizedPhone = normalizePhoneKE(billingPopupPhone);

    if (!/^254\d{9}$/.test(normalizedPhone)) {
      setBillingPopupError('Please enter a valid Kenyan phone number in the format 2547XXXXXXXX.');
      return;
    }

    if (!billingPopupAmount || billingPopupAmount <= 0) {
      setBillingPopupError('No billable amount is available for this account yet.');
      return;
    }

    try {
      setBillingPopupSubmitting(true);
      setBillingPopupError(null);
      const res = await billingService.initiateRenewal(normalizedPhone, '1.0');
      setBillingPopupAmount(res.data.amount);
      setBillingPopupPhone(res.data.phone);
      setBillingPopupInvoiceId(res.data.invoiceId);
      setBillingPopupTransactionId(res.data.transactionId);
      setBillingPopupMessage(`STK push sent to ${res.data.phone}. Enter your M-Pesa PIN to complete the renewal payment.`);
      toast.success('STK push sent', { description: `A payment prompt has been sent to ${res.data.phone}.` });
    } catch (error: any) {
      setBillingPopupError(error?.response?.data?.error || error?.message || 'Could not initiate billing payment.');
    } finally {
      setBillingPopupSubmitting(false);
    }
  };

  // Re-check billing after settings modal closes (landlord may have just paid)
  const handleSettingsClose = useCallback(() => {
    const shouldRecheckBilling = billingWall.active || settingsInitialSection === 'billing';
    setIsSettingsModalOpen(false);
    setSettingsInitialSection(null);
    billingCheckedRef.current = false;
    setTimeout(() => {
      billingCheckedRef.current = false;
    }, 500);
    if (shouldRecheckBilling) {
      setBillingChecking(true);
      authTokenService.getToken() && fetch(`${API_BASE_URL}/billing/status`, {
        headers: { Authorization: `Bearer ${authTokenService.getToken()}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.gate?.allowed === true) {
            setBillingWall({ active: false });
          } else if (data?.gate?.allowed === false) {
            setBillingWall({
              active: true,
              reason: data.gate.reason,
              message: getBillingWallMessage(data.gate.reason),
              redirectTo: data.gate.redirectTo,
            });
          }
        })
        .catch(() => {})
        .finally(() => setBillingChecking(false));
    }
  }, [billingWall.active, settingsInitialSection]);

  useCloseOnRouteChange(isSettingsModalOpen, handleSettingsClose);

  const dismissOverlaysForNavigation = useCallback(() => {
    if (isSettingsModalOpen) {
      handleSettingsClose();
    }
  }, [isSettingsModalOpen, handleSettingsClose]);

  const navigateToPage = useCallback((href: string) => {
    dismissOverlaysForNavigation();
    navigate(href);
    setMobileMenuOpen(false);
  }, [dismissOverlaysForNavigation, navigate]);

  // Open settings modal to a specific tab when URL has ?openSettings=billing (e.g. after billing gate 403)
  // Only open if user has canAccessSettings permission
  useEffect(() => {
    const section = searchParams.get('openSettings');
    if (section && canAccess('settings')) {
      setSettingsInitialSection(section);
      setIsSettingsModalOpen(true);
      setSearchParams({}, { replace: true });
    } else if (section && !canAccess('settings')) {
      // Clear the param if user doesn't have permission
      setSearchParams({}, { replace: true });
      toast.error('Access Denied', {
        description: 'You do not have permission to access settings.',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams, canAccess]);

  // Helper function to get user initials
  const getUserInitials = (): string => {
    if (user?.displayName) {
      const names = user.displayName.split(' ');
      if (names.length >= 2) {
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
      }
      return names[0].substring(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  // Helper function to get user display name
  const getUserDisplayName = (): string => {
    return user?.displayName || user?.email?.split('@')[0] || 'User';
  };

  // Helper function to get user email
  const getUserEmail = (): string => {
    return user?.email || 'No email';
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
    };

    if (userDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userDropdownOpen]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('[data-mobile-menu]') && !target.closest('[data-mobile-button]')) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const handleInstallClick = async () => {
    if (isInstalled) {
      toast.info('App already installed', {
        description: 'TenantPlus is already installed on your device.',
      });
      setMobileMenuOpen(false);
      return;
    }

    const result = await handleInstall();

    if (result.success) {
      toast.success('App installed successfully!', {
        description: 'You can now access TenantPlus from your home screen.',
      });
      setMobileMenuOpen(false);
      return;
    }

    // Handle different failure scenarios
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isAndroid = /Android/.test(navigator.userAgent);
    const isChrome = /Chrome/.test(navigator.userAgent) && !/Edge|Edg/.test(navigator.userAgent);
    const isEdge = /Edge|Edg/.test(navigator.userAgent);
    const isFirefox = /Firefox/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);

    if (result.message === 'ios_instructions') {
      toast.info('Install TenantPlus on iOS', {
        description: 'Tap the Share button (square with arrow) at the bottom, then select "Add to Home Screen"',
        duration: 8000,
      });
    } else if (result.message === 'install_instructions_ready') {
      // All criteria met but browser hasn't fired the event yet
      if (isChrome || isEdge) {
        toast.info('Install TenantPlus', {
          description: 'All requirements are met! Look for the install icon (⊕) in your browser\'s address bar, or use the menu (⋮) → "Install TenantPlus". The browser may show the prompt after you visit a few more times.',
          duration: 10000,
        });
      } else if (isFirefox) {
        toast.info('Install TenantPlus', {
          description: 'All requirements are met! Click the menu (☰) → "Install" or look for the install icon in the address bar.',
          duration: 10000,
        });
      } else {
        toast.info('Install TenantPlus', {
          description: 'All requirements are met! Use your browser\'s install option from the menu or address bar.',
          duration: 10000,
        });
      }
    } else if (result.message === 'install_instructions') {
      // Provide browser-specific instructions
      if (isChrome || isEdge) {
        toast.info('Install TenantPlus', {
          description: deferredPrompt
            ? 'The install prompt should appear. If not, look for the install icon (⊕) in your browser\'s address bar.'
            : 'Look for the install icon (⊕) in your browser\'s address bar, or use the menu (⋮) → "Install TenantPlus"',
          duration: 8000,
        });
      } else if (isFirefox) {
        toast.info('Install TenantPlus', {
          description: 'Click the menu (☰) → "Install" or look for the install icon in the address bar',
          duration: 8000,
        });
      } else if (isSafari) {
        toast.info('Install TenantPlus', {
          description: 'Safari doesn\'t support PWA installation. Please use Chrome or Edge.',
          duration: 8000,
        });
      } else if (isAndroid) {
        toast.info('Install TenantPlus', {
          description: 'Use your browser menu → "Add to Home Screen" or "Install App"',
          duration: 8000,
        });
      } else {
        toast.info('Install TenantPlus', {
          description: result.message || 'Look for the install option in your browser menu or address bar',
          duration: 8000,
        });
      }
    } else if (result.message) {
      // Show specific error message
      toast.warning('Installation Issue', {
        description: result.message,
        duration: 8000,
      });
    } else {
      // Generic fallback
      toast.info('Install TenantPlus', {
        description: 'Please use your browser\'s install option from the menu or address bar',
        duration: 8000,
      });
    }

    setMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;

    setIsLoggingOut(true);
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Filter navigation based on permissions
  const allNavigationItems = [
    { name: 'Properties', href: '/', icon: Building2, permission: 'properties' as const },
    { name: 'Tenants', href: '/tenants', icon: Users, permission: 'tenants' as const },
    { name: 'Communications', href: '/messages', icon: MessageSquare, permission: null }, // Communications accessible to all authenticated users
    { name: 'Payments', href: '/payments', icon: CreditCard, permission: 'payments' as const },
  ];

  const navigation = allNavigationItems.filter(item =>
    item.permission === null || canAccess(item.permission)
  ).map(({ permission, ...item }) => item);

  const settingsDrawerItems = [
    { id: 'account', label: 'Account', icon: User },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'payment-channels', label: 'Payment Channels', icon: DollarSign },
    { id: 'team', label: 'Team', icon: Users },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Settings },
    { id: 'preferences', label: 'Preferences', icon: Globe },
    { id: 'activity', label: 'Activity Log', icon: Calendar },
  ] as const;

  const openSettingsSection = (section?: string) => {
    if (canAccess('settings')) {
      setSettingsInitialSection(section ?? null);
      setIsSettingsModalOpen(true);
      setUserDropdownOpen(false);
      setMobileMenuOpen(false);
      return;
    }

    toast.error('Access Denied', {
      description: 'You do not have permission to access settings.',
    });
    setUserDropdownOpen(false);
    setMobileMenuOpen(false);
  };

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <LayoutContainer>
      <Navbar>
        <NavbarContainer>
          <NavbarContent>
            <NavbarLeft>
              <Logo>
                <LogoIcon />
                <LogoText>
                  Tenant<LogoPlus>Plus</LogoPlus>
                </LogoText>
              </Logo>

              <DesktopNavigation>
                {navigation.map((item) => (
                  <NavLink
                    key={item.name}
                    $active={isActive(item.href)}
                    onClick={() => navigateToPage(item.href)}
                  >
                    <item.icon />
                    {item.name}
                  </NavLink>
                ))}
              </DesktopNavigation>
            </NavbarLeft>

            <NavbarRight>
              <DesktopActions onClick={(e) => e.stopPropagation()}>
                <ThemeToggle />
              </DesktopActions>
              {/* Bell always visible so it works on small screens; drawer "Notifications" uses ?openNotifications=1 */}
              <NotificationPanel />

              <UserMenuContainer ref={dropdownRef}>
                <UserAvatar onClick={() => setUserDropdownOpen(!userDropdownOpen)}>
                  {getUserInitials()}
                </UserAvatar>

                <DropdownMenu $isOpen={userDropdownOpen}>
                  <DropdownHeader>
                    <UserName>{getUserDisplayName()}</UserName>
                    <UserEmail>{getUserEmail()}</UserEmail>
                  </DropdownHeader>

                  <DropdownItem onClick={() => openSettingsSection('account')}>
                    <Settings size={16} />
                    Settings
                  </DropdownItem>

                  <DropdownItem
                    onClick={() => {
                      navigateToPage('/billing');
                      setUserDropdownOpen(false);
                    }}
                  >
                    <CreditCard size={16} />
                    Billing
                  </DropdownItem>

                  <DropdownItem onClick={handleLogout} disabled={isLoggingOut}>
                    <LogOut size={16} />
                    {isLoggingOut ? 'Logging out...' : 'Log out'}
                  </DropdownItem>
                </DropdownMenu>
              </UserMenuContainer>

              <MobileMenuButton
                data-mobile-button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <Menu size={20} />
              </MobileMenuButton>
            </NavbarRight>
          </NavbarContent>
        </NavbarContainer>
      </Navbar>

      <NotificationPermissionPrompt showWhenDefault={true} />
      <EnableNotificationsModal isStandalone={isInstalled} />

      <Overlay $isOpen={mobileMenuOpen} onClick={() => setMobileMenuOpen(false)} />

      <NavigationDrawer data-mobile-menu $isOpen={mobileMenuOpen}>
        <DrawerHeader>
          <DrawerLogo>
            <DrawerLogoIcon />
            <DrawerLogoText>
              Tenant<DrawerLogoPlus>Plus</DrawerLogoPlus>
            </DrawerLogoText>
          </DrawerLogo>
          <CloseButton onClick={() => setMobileMenuOpen(false)}>
            ×
          </CloseButton>
        </DrawerHeader>

        <DrawerContent>
          <DrawerNavSection>
            {navigation.map((item) => (
              <DrawerNavItem
                key={item.name}
                $active={isActive(item.href)}
                onClick={() => navigateToPage(item.href)}
              >
                <item.icon />
                {item.name}
              </DrawerNavItem>
            ))}
            <DrawerNavItem
              $active={location.pathname.startsWith('/billing')}
              onClick={() => navigateToPage('/billing')}
            >
              <CreditCard size={18} />
              Billing
            </DrawerNavItem>
          </DrawerNavSection>

          <DrawerNavSection>
            <DrawerSectionTitle>Settings</DrawerSectionTitle>
            {settingsDrawerItems.map((item) => (
              <DrawerNavItem
                key={item.id}
                onClick={() => openSettingsSection(item.id)}
              >
                <item.icon />
                {item.label}
              </DrawerNavItem>
            ))}
          </DrawerNavSection>
        </DrawerContent>

        <DrawerUserSection>
          <DrawerUserInfo>
            <DrawerUserAvatar>
              {getUserInitials()}
            </DrawerUserAvatar>
            <DrawerUserDetails>
              <DrawerUserName>{getUserDisplayName()}</DrawerUserName>
              <DrawerUserEmail>{getUserEmail()}</DrawerUserEmail>
            </DrawerUserDetails>
          </DrawerUserInfo>

          <DrawerOption
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dismissOverlaysForNavigation();
              const search = new URLSearchParams(location.search);
              search.set('openNotifications', '1');
              navigate(`${location.pathname}?${search.toString()}`, { replace: false });
              setMobileMenuOpen(false);
            }}
          >
            <DrawerOptionText>Notifications</DrawerOptionText>
            <DrawerOptionIcon>
              <div className="icon-wrapper">
                <Bell size={16} />
                <NotificationBadge>3</NotificationBadge>
              </div>
            </DrawerOptionIcon>
          </DrawerOption>

          <DrawerOption>
            <DrawerOptionText>Theme</DrawerOptionText>
            <DrawerOptionIcon>
              <ThemeToggle />
            </DrawerOptionIcon>
          </DrawerOption>

          {canShowButton && (
            <DrawerOption
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!isInstalled) {
                  handleInstallClick();
                } else {
                  toast.info('App already installed', {
                    description: 'TenantPlus is already installed on your device.',
                  });
                }
                setMobileMenuOpen(false);
              }}
            >
              <DrawerOptionText>
                {isInstalled ? 'Installed' : 'Install App'}
              </DrawerOptionText>
              <DrawerOptionIcon>
                <Download size={16} />
              </DrawerOptionIcon>
            </DrawerOption>
          )}
        </DrawerUserSection>

        <DrawerFooter>
          <DrawerFooterItem onClick={handleLogout} disabled={isLoggingOut}>
            <LogOut size={16} />
            {isLoggingOut ? 'Logging out...' : 'Log out'}
          </DrawerFooterItem>
        </DrawerFooter>
      </NavigationDrawer>

      <MainContent>
        <Outlet />
      </MainContent>

      {/* Landlord Settings Modal - Only render if user has permission */}
      {canAccess('settings') && (
        <LandlordSettingsModal
          isOpen={isSettingsModalOpen}
          onClose={handleSettingsClose}
          initialSection={settingsInitialSection ?? undefined}
        />
      )}

      {/* ── Billing Wall ──────────────────────────────────────────────────────
           Only visible to landlord-side users with an expired subscription.
           Tenants use a completely different layout and never see this. */}
      {billingWall.active && user?.role !== 'TENANT' && (
        <BillingWallOverlay>
          <BillingWallCard>
            <BillingWallBrand>Tenant<span>Plus</span></BillingWallBrand>
            <BillingWallIcon>
              <AlertCircle size={32} />
            </BillingWallIcon>
            <BillingWallTitle>Subscription Required</BillingWallTitle>
            <BillingWallBody>
              {billingWall.message || 'Your subscription is inactive. Please update your billing to restore full access.'}
            </BillingWallBody>
            <BillingWallBtnRow>
              <BillingWallPrimaryBtn
                onClick={() => {
                  void openBillingPopup();
                }}
                disabled={billingChecking || billingPopupLoading}
              >
                <CreditCard size={16} />
                {billingPopupLoading ? 'Loading Billing…' : 'Manage Billing'}
              </BillingWallPrimaryBtn>
              <BillingWallPrimaryBtn
                onClick={() => {
                  setBillingChecking(true);
                  billingCheckedRef.current = false;
                  fetch(`${API_BASE_URL}/billing/status`, {
                    headers: { Authorization: `Bearer ${authTokenService.getToken()}` },
                  })
                    .then(r => r.ok ? r.json() : null)
                    .then(data => {
                      if (data?.gate?.allowed === true) {
                        setBillingWall({ active: false });
                        toast.success('Subscription restored', { description: 'Your account is now active.' });
                      } else {
                        toast.info('Subscription still inactive', { description: 'Please complete your billing setup.' });
                      }
                    })
                    .catch(() => toast.error('Could not check subscription status', { description: 'Please try again.' }))
                    .finally(() => setBillingChecking(false));
                }}
                disabled={billingChecking}
                style={{ background: 'transparent', border: '1px solid', borderColor: 'var(--border)', color: 'var(--foreground)' }}
              >
                <RefreshCw size={15} style={billingChecking ? { animation: 'spin 1s linear infinite' } : undefined} />
                {billingChecking ? 'Checking…' : 'Check Again'}
              </BillingWallPrimaryBtn>
              <BillingWallSecondaryBtn onClick={handleLogout} disabled={isLoggingOut}>
                <LogOut size={15} />
                {isLoggingOut ? 'Signing out…' : 'Sign Out'}
              </BillingWallSecondaryBtn>
            </BillingWallBtnRow>
          </BillingWallCard>
        </BillingWallOverlay>
      )}

      {billingWall.active && billingPopupOpen && user?.role !== 'TENANT' && (
        <BillingPopupBackdrop>
          <BillingPopupCard>
            <BillingPopupHeader>
              <div>
                <BillingPopupTitle>Complete Subscription Payment</BillingPopupTitle>
                <BillingPopupBody>
                  Billing is based on your current unit count. Pay the amount below to restore access.
                </BillingPopupBody>
              </div>
              <BillingPopupClose onClick={() => setBillingPopupOpen(false)} aria-label="Close billing popup">
                <X size={18} />
              </BillingPopupClose>
            </BillingPopupHeader>

            <BillingAmountCard>
              <BillingAmountLabel>Amount Due</BillingAmountLabel>
              <BillingAmountValue>
                {billingPopupLoading
                  ? 'Loading…'
                  : billingPopupAmount != null
                    ? `KES ${billingPopupAmount.toLocaleString()}`
                    : '—'}
              </BillingAmountValue>
              {!billingPopupLoading && billingPopupUsedMinimum && (
                <BillingHelperText style={{ marginTop: 6 }}>
                  Minimum activation fee (1 unit × KES {billingPopupAmount ?? '—'}). Billing adjusts as you add units.
                </BillingHelperText>
              )}
            </BillingAmountCard>

            <BillingField>
              <BillingFieldLabel htmlFor="billing-phone">M-Pesa Phone Number</BillingFieldLabel>
              <BillingInput
                id="billing-phone"
                type="tel"
                value={billingPopupPhone}
                onChange={(e) => setBillingPopupPhone(formatPhoneKEForInput(e.target.value))}
                placeholder="254700000000"
                disabled={billingPopupLoading || billingPopupSubmitting || billingPopupPolling}
              />
              <BillingHelperText>
                Enter the number that should receive the STK push prompt.
              </BillingHelperText>
            </BillingField>

            {billingPopupMessage && <BillingInlineMessage>{billingPopupMessage}</BillingInlineMessage>}
            {billingPopupError && <BillingInlineMessage $error>{billingPopupError}</BillingInlineMessage>}

            <BillingWallBtnRow>
              <BillingWallPrimaryBtn
                onClick={() => { void initiateBillingPayment(); }}
                disabled={billingPopupLoading || billingPopupSubmitting || billingPopupPolling || !billingPopupAmount || billingPopupAmount <= 0}
              >
                <CreditCard size={16} />
                {billingPopupLoading ? 'Loading Amount…' : billingPopupSubmitting ? 'Sending STK Push…' : 'Pay Now'}
              </BillingWallPrimaryBtn>

              {billingPopupInvoiceId && (
                <BillingWallPrimaryBtn
                  onClick={() => { void checkBillingPaymentStatus(true); }}
                  disabled={billingPopupSubmitting || billingPopupPolling}
                  style={{ background: 'transparent', border: '1px solid', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                >
                  <RefreshCw size={15} style={billingPopupPolling ? { animation: 'spin 1s linear infinite' } : undefined} />
                  {billingPopupPolling ? 'Checking…' : 'Check Payment Status'}
                </BillingWallPrimaryBtn>
              )}
            </BillingWallBtnRow>
          </BillingPopupCard>
        </BillingPopupBackdrop>
      )}
    </LayoutContainer>
  );
};

export default ModernLayout; 